import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * DeliveryJourneySidebar
 * ─────────────────────────────────────────────────────────────────
 * A slim right-edge sidebar that the user sees while filling in the
 * 8 key sections needed to generate a meaningful delivery plan.
 *
 * Triggered by the readiness screen on Delivery Guru: clicking
 * "Go there now" sets `guruone_delivery_journey: { active: true,
 * returnPath }` in localStorage. This component reads that flag on
 * mount and only renders when it is active.
 *
 * The 8 sections mirror the readiness checks in DeliveryGuru.js. If
 * either source changes the list, the other should change too.
 *
 * Status indicator:
 *   filled  = green tick
 *   partial = amber dot
 *   empty   = grey ring
 */

const JOURNEY_KEY = 'guruone_delivery_journey';
const STRATEGY_STORAGE_KEY = 'guruone_strategy';
const OM_STORAGE_KEY = 'guruone_operating_model';
const RETURN_PATH = '/delivery';
// The sidebar is mounted independently on every canvas page, so a
// plain useState for "dismissed" gets reset on every navigation. Use
// sessionStorage so the dismissal survives route changes within the
// same browser session, while still re-appearing on a fresh session.
const CLOSED_FOR_SESSION_KEY = 'guruone_delivery_journey_closed_session';

const SECTIONS = [
  { canvas: 'direction', key: 'vision',                title: 'Vision',                  icon: '◉', route: '/strategy' },
  { canvas: 'direction', key: 'customers',             title: 'Customers',               icon: '◕', route: '/strategy' },
  { canvas: 'direction', key: 'value_proposition',     title: 'Value Proposition',       icon: '◆', route: '/strategy' },
  { canvas: 'direction', key: 'current_challenges',    title: 'Current Challenges',      icon: '⚡', route: '/strategy' },
  { canvas: 'design',    key: 'people_roles',          title: 'People, Roles & Structure', icon: '👥', route: '/operating-model' },
  { canvas: 'design',    key: 'finance_commercial',    title: 'Finance & Commercial',    icon: '£', route: '/operating-model' },
  { canvas: 'design',    key: 'process_ways_of_work',  title: 'Process & Ways of Work',  icon: '⚙', route: '/operating-model' },
  { canvas: 'design',    key: 'customer_interactions', title: 'Customer Interactions',   icon: '💬', route: '/operating-model' },
];

function readJourney() {
  try {
    const raw = localStorage.getItem(JOURNEY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : null;
  } catch { return null; }
}

function readUser() {
  try { return JSON.parse(localStorage.getItem('guruone_user')); } catch { return null; }
}

function readUserScopedCanvas(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const all = JSON.parse(raw);
    if (!all || typeof all !== 'object') return {};
    const user = readUser();
    const userKey = user?.id ? `user_${user.id}` : null;
    if (userKey && all[userKey]) return all[userKey];
    // Fall back to the largest entry (matches Dashboard's loadStrategyData).
    const entries = Object.values(all).filter(v => v && typeof v === 'object');
    return entries.reduce(
      (best, curr) => Object.keys(curr).length > Object.keys(best).length ? curr : best,
      {}
    );
  } catch { return {}; }
}

function getSectionState(value) {
  if (!value) return 'empty';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length > 8) return 'filled';
    if (trimmed.length > 0) return 'partial';
    return 'empty';
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? 'filled' : 'empty';
  }
  if (typeof value === 'object') {
    const filledCount = Object.values(value).filter(v => {
      if (v === null || v === undefined) return false;
      if (typeof v === 'string') return v.trim().length > 0;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'object') return Object.keys(v).length > 0;
      return !!v;
    }).length;
    if (filledCount === 0) return 'empty';
    if (filledCount >= 3) return 'filled';
    return 'partial';
  }
  return 'empty';
}

const STATE_PALETTE = {
  filled:  { colour: '#1D9E75', solid: true,  glyph: '✓' },
  partial: { colour: '#D97706', solid: true,  glyph: '' },
  empty:   { colour: '#cbd5e1', solid: false, glyph: '' },
};

export default function DeliveryJourneySidebar() {
  const navigate = useNavigate();
  const [journey, setJourney] = useState(() => readJourney());
  // Seed from sessionStorage so dismissal survives navigation between
  // Direction and Design pages within the same browser session.
  const [closedForSession, setClosedForSessionState] = useState(() => {
    try { return sessionStorage.getItem(CLOSED_FOR_SESSION_KEY) === '1'; } catch { return false; }
  });
  const setClosedForSession = (v) => {
    setClosedForSessionState(v);
    try {
      if (v) sessionStorage.setItem(CLOSED_FOR_SESSION_KEY, '1');
      else sessionStorage.removeItem(CLOSED_FOR_SESSION_KEY);
    } catch { /* private mode etc — non-fatal */ }
  };
  const [strategyData, setStrategyData] = useState(() => readUserScopedCanvas(STRATEGY_STORAGE_KEY));
  const [omData, setOmData] = useState(() => readUserScopedCanvas(OM_STORAGE_KEY));

  // Sync with cross-tab localStorage changes (e.g. user edits a canvas
  // section in another tab — the sidebar's status dots update on focus).
  useEffect(() => {
    const handler = (e) => {
      if (e.key === JOURNEY_KEY) setJourney(readJourney());
      if (e.key === STRATEGY_STORAGE_KEY) setStrategyData(readUserScopedCanvas(STRATEGY_STORAGE_KEY));
      if (e.key === OM_STORAGE_KEY) setOmData(readUserScopedCanvas(OM_STORAGE_KEY));
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  if (!journey?.active || closedForSession) return null;

  const handleReturn = () => {
    try { localStorage.removeItem(JOURNEY_KEY); } catch { /* non-fatal */ }
    // Reset the per-session dismissal too — if a leader later starts
    // a fresh delivery journey, it should appear without needing to
    // open a new browser session first.
    try { sessionStorage.removeItem(CLOSED_FOR_SESSION_KEY); } catch { /* non-fatal */ }
    setJourney(null);
    navigate(journey.returnPath || RETURN_PATH);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 54,           // sit beneath the sticky Header (Header.js height = 54)
      right: 0,
      bottom: 0,
      width: 220,
      background: 'white',
      borderLeft: '1px solid #eee',
      boxShadow: '-4px 0 12px rgba(0,0,0,0.04)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 90,
      fontFamily: 'inherit',
    }}>
      <div style={{
        position: 'relative',
        padding: '0.85rem 2rem 0.6rem 1rem',
        borderBottom: '1px solid #f0f0f0',
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa', whiteSpace: 'nowrap' }}>
            Delivery journey
          </div>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1a1a2e', marginTop: 2, whiteSpace: 'nowrap' }}>
            8 key sections
          </div>
        </div>
        <button
          onClick={() => setClosedForSession(true)}
          aria-label="Hide for this session"
          title="Hide for this session"
          style={{
            position: 'absolute', top: 6, right: 6,
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '1rem', color: '#aaa', padding: '0.2rem 0.45rem',
            lineHeight: 1, fontFamily: 'inherit', width: 'auto',
          }}
        >
          ×
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem 0' }}>
        {SECTIONS.map(section => {
          const data = section.canvas === 'direction' ? strategyData[section.key] : omData[section.key];
          const state = getSectionState(data);
          const palette = STATE_PALETTE[state];
          return (
            <button
              key={section.key}
              onClick={() => navigate(section.route)}
              style={{
                width: '100%',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.55rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem',
                textAlign: 'left',
                fontFamily: 'inherit',
                color: 'inherit',
              }}
            >
              <span style={{ fontSize: '0.95rem', flexShrink: 0, width: 18, textAlign: 'center', color: '#555' }}>
                {section.icon}
              </span>
              <span style={{ flex: 1, fontSize: '0.78rem', color: '#1a1a2e', minWidth: 0, lineHeight: 1.3 }}>
                {section.title}
              </span>
              <span
                aria-label={`status: ${state}`}
                style={{
                  flexShrink: 0,
                  width: 14, height: 14, borderRadius: '50%',
                  background: palette.solid ? palette.colour : 'transparent',
                  border: palette.solid ? 'none' : `1.5px solid ${palette.colour}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 9, color: 'white', fontWeight: 800, lineHeight: 1,
                }}
              >
                {palette.glyph}
              </span>
            </button>
          );
        })}
      </div>

      <div style={{ padding: '0.75rem 1rem 1rem', borderTop: '1px solid #f0f0f0' }}>
        <button
          onClick={handleReturn}
          style={{
            width: '100%',
            padding: '0.65rem 0.9rem',
            borderRadius: 8,
            background: '#D97706',
            color: 'white',
            border: 'none',
            fontSize: '0.78rem',
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Return to Delivery Guru →
        </button>
      </div>
    </div>
  );
}
